import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { DatePicker } from "@/components/ui/date-picker"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Search, X, Trash2, AlertTriangle, Filter } from "lucide-react"
import { AnalyticsFiltersProps } from "../types"
import { useTranslation } from "react-i18next"

export const AnalyticsFilters = ({
  startDate,
  endDate,
  searchQuery,
  selectedRecords,
  isDeleting,
  hasActiveFilters,
  onStartDateChange,
  onEndDateChange,
  onSearchQueryChange,
  onClearFilters,
  onDeleteSelected,
  onDeleteAll,
}: AnalyticsFiltersProps) => {
  const {t} = useTranslation("analytics")
  return (
    <div className="flex flex-col lg:flex-row gap-4 p-4 bg-muted/20 rounded-lg">
      {/* Left side - Filters */}
      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t("analytics:filter.searchPlaceholder")}
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            className="pl-10 h-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
              onClick={() => onSearchQueryChange("")}
            >
              <X className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Date Range */}
        <DatePicker
          date={startDate}
          onDateChange={onStartDateChange}
          placeholder={t("analytics:filter.startDatePlaceholder")}
          className="h-9"
        />
        <DatePicker
          date={endDate}
          onDateChange={onEndDateChange}
          placeholder={t("analytics:filter.endDatePlaceholder")}
          className="h-9"
        />
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2 lg:border-l lg:pl-4">
        {selectedRecords.size > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" disabled={isDeleting}>
                <Trash2 className="h-4 w-4 mr-1" />
                {t("analytics:filter.delete")} ({selectedRecords.size})
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t("analytics:filter.deleteSelected")}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t("analytics:filter.deleteselectedConfirm", {selectedRecordsSize:selectedRecords.size})}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("analytics:filter.cancel")}</AlertDialogCancel>
                <AlertDialogAction onClick={onDeleteSelected} className="bg-destructive hover:bg-destructive/90">
                  {t("analytics:filter.deleteSessions")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" size="sm" disabled={isDeleting}>
              <AlertTriangle className="h-4 w-4 mr-1" />
              {t("analytics:filter.deleteAll")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t("analytics:filter.deleteAllSessions")}</AlertDialogTitle>
              <AlertDialogDescription>
                {t("analytics:filter.deleteAllConfirm")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t("analytics:filter.cancel")}</AlertDialogCancel>
              <AlertDialogAction onClick={onDeleteAll} className="bg-destructive hover:bg-destructive/90">
                {t("analytics:filter.deleteAllSessions")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Button variant="outline" size="sm" onClick={onClearFilters} disabled={!hasActiveFilters}>
          <Filter className="h-4 w-4 mr-1" />
          {t("analytics:filter.clear")}
        </Button>
      </div>
    </div>
  )
}
